import { get, post } from '@/utils/request'
import { encrypt } from '@/utils/crypto'

/**
 * @typedef {Object} MsgBean - 消息对象
 * @property {string} msg_id - 消息ID (Primary Key)
 * @property {number} seq_id - 序列号（用于分页）
 * @property {string} conversation_id - 会话ID
 * @property {string} send_uid - 发送者ID
 * @property {number} created_at - 创建时间戳
 * @property {string} sub_type - 消息类型
 * @property {string} content - 完整的消息内容 JSON 字符串，包含该消息类型的所有字段。例如文本消息包含文本内容、翻译内容等；图片消息包含图片 URL、缩略图等。
 * @property {string} msg_preview - 消息预览
 * @property {boolean} say_hello - 是否为打招呼消息(陌生人)
 * @property {string} encryption_type - 加密类型
 */

/**
 * @typedef {Object} ConversationMsgBean
 * @property {boolean} is_over - 是否已经没有更多历史消息
 * @property {MsgBean[]} list - 消息列表，倒序（最新在前）
 */

/**
 * 拉取会话消息(历史消息)
 * 用法一：增量拉取新消息（不传 conversation_id）
 * 使用场景: 用户刚登录，或需要同步所有会话的离线消息/新消息
 * 用法二：查询单个会话的历史消息（传 conversation_id）
 * 使用场景: 用户进入某个聊天界面，查看该会话的历史消息（上拉加载更多）
 * @param {Object} params - 请求参数
 * @param {string} params.conversation_id - 会话ID
 * @param {number} [params.before_seq_id] - 早于此seq_id的消息（用于分页）
 * @param {number} [params.size=20] - 每次获取数量
 * @returns {Promise<ConversationMsgBean>}
 */
export function getChatMessages(params) {
  return get('/msg/v1/chat/msg', params, {
    hideLoading: true, // 静默加载，不显示全局loading
  })
}

/**
 * @typedef {Object} ConversationBean - 会话对象（实际为 ChatListItem）
 *
 * 基础会话信息
 * @property {string} conversation_id - 会话ID（加密）
 * @property {string} conversation_type - 会话类型（目前固定为 "Normal"）
 * @property {number} created_at - 会话创建时间（毫秒时间戳）
 * @property {number} updated_at - 会话更新时间（毫秒时间戳）
 * @property {string} chat_source - 会话来源
 *  chat_source = "PrivateMsg"  // 私信列表
 *  chat_source = "UserInfo"  // 用户个人页
 *  chat_source = "PartyRoom"  // 语音房
 *  chat_source = "ShowRoom"  // 语音房
 *  chat_source = "MomentNotification"  // 动态通知
 * @property {number} chat_depth - 会话深度
 * @property {string} status - 会话状态 status = 1: 正常状态 status = -1: 已删除状态
 * @property {number} rank - 排序值（用于置顶，值越大越靠前）
 * @property {string} preview - 消息预览（AES加密）
 * @property {number} last_msg_at - 最后一条消息时间（毫秒时间戳）
 *
 * 对方用户信息
 * @property {string} target_uid - 对方用户ID（加密）
 * @property {string} target_nick_name - 对方昵称
 * @property {string} target_avatar_url - 对方头像URL
 * @property {string} target_status - 对方用户状态
 * @property {number} target_sex - 对方性别
 * @property {number} target_role - 对方角色
 * target_role = 0: 普通用户（Role_Common）
 * target_role = 1: 主播（Role_Anchor）
 * target_role = 2: 运营（Role_Operator）
 * target_role = 10: 白名单用户（Role_WhiteList）
 * target_role = 12: FY用户（Role_FYUser）
 * @property {string} target_avatar_mark - 对方头像标记
 * @property {string} target_note_name - 对方备注名
 *
 * 阅读状态
 * @property {number} member_read_at - 当前用户已读时间（毫秒时间戳）
 * @property {number} target_read_at - 对方已读时间（毫秒时间戳）
 *
 * 亲密度信息
 * @property {number} intimacy_level - 亲密度等级
 * @property {number} intimacy_value - 亲密度值
 *
 * 其他
 * @property {boolean} say_hello - 是否为打招呼消息
 * @property {string} encryption_type - 加密类型
 * @property {number} [unreadCount] - 未读数（客户端计算）
 * @property {boolean} [openTranslate] - 是否开启翻译（客户端扩展）
 * @property {boolean} [showQuickMsg] - 是否显示快捷消息（客户端扩展）
 */

/**
 * @typedef {Object} SyncConversationBean
 * @property {boolean} is_over - 是否已经没有更多会话
 * @property {ConversationBean[]} list - 会话列表
 */

/**
 * 拉取会话列表
 * 1. 分页模式 传递 page 和 size
 * 2. 精确查找模式 传递 conversation_ids
 * @param {Object} params - 请求参数
 * @param {number} [params.page=1] - 页码
 * @param {number} [params.size=20] - 每次获取数量
 * @param {string[]} [params.conversation_ids] - 加密的会话ID列表（必填）
 * @returns {Promise<SyncConversationBean>}
 */
export function getConversationList(params) {
  return get('/msg/v1/chat/list', {
    ...params,
    conversation_ids: params.conversation_ids.join(','),
  }, {
    hideLoading: true, // 静默加载，不显示全局loading
  })
}

/**
 * 置顶会话
 * @param {Object} data - 请求参数
 * @param {string} data.conversation_id - 加密的会话ID（必填）
 * @param {number} data.rank - 0: 取消置顶, 1: 设置置顶（必填）
 * @returns {Promise<{code: number, error: string, data: {}}>}
 */
export function setTopConversation(data) {
  return post('/msg/v1/chat/set_top', data, {
    hideLoading: true,
  })
}

/**
 * 删除会话
 * @param {Object} data - 请求参数
 * @param {string} data.target_id - 加密的对方用户ID（必填）
 * @param {string} data.chat_id - 加密的会话ID（必填）
 * @param {string} data.seq_id - 消息序列号（必填，可以为"0"表示删除整个会话）
 * @returns {Promise<{code: number, error: string}>}
 */
export function deleteConversation(data) {
  return post('/msg/v1/delete_chat', data, {
    hideLoading: true,
  })
}

/**
 * @typedef {Object} SendMessageResponse
 * @property {number} code - 响应码
 * @property {string} error - 错误信息
 * @property {Object} data - 响应数据
 * @property {string} data.id - 消息ID
 * @property {string} data.msg_id - 消息ID
 * @property {string} data.conversation_id - 会话ID
 * @property {number} data.seq_id - 消息序列号
 * @property {string} data.member_id - 会员ID
 * @property {string} data.send_uid - 加密的发送者ID
 * @property {string} data.sub_type - 消息子类型
 * @property {string} data.meta_type - 消息子类型
 * @property {string} data.encryption_type - 加密类型
 * @property {string} data.msg_source - 消息来源
 * @property {string} data.content - 加密的消息JSON内容
 * @property {string} data.created_at - 创建时间戳（毫秒）
 * @property {string} data.msg_preview - 加密的消息预览
 * @property {Object} data.conversation - 会话信息
 * @property {string} data.conversation.id - 加密的会话ID
 * @property {number} data.conversation.rank - 置顶标识 0:未置顶 1:置顶
 * @property {number} data.conversation.intimacy_level - 亲密度等级
 * @property {boolean} data.conversation.hide_flag - 是否隐藏
 * @property {number} data.conversation.parent
 * @property {string} data.conversation.conversation_type - 会话类型
 * @property {string} data.conversation.target_read_at - 对方已读时间
 * @property {string} data.conversation.member_read_at - 自己已读时间
 * @property {string} data.conversation.create_timestamp - 创建时间戳
 * @property {number} data.conversation.valid_rounds - 会话深度
 * @property {number} data.conversation.chat_source - 会话来源
 * @property {object} data.conversation.user - 对方用户信息
 * @property {object} data.conversation.user.age - 年龄
 * @property {object} data.conversation.user.avatar_url - 对方用户信息
 * @property {object} data.conversation.user.birthday - 生日
 * @property {object} data.conversation.user.id - 用户ID
 * @property {object} data.conversation.user.location - 位置
 * @property {object} data.conversation.user.member_id - 会员ID
 * @property {object} data.conversation.user.nick_name - 昵称
 * @property {object} data.conversation.user.online - 在线状态
 * @property {object} data.conversation.user.sex - 性别
 */

/**
 * 发送消息（新版接口）
 * 支持文本、图片、视频等多种消息类型
 * @param {FormData|Object} data - 请求参数
 * @param {string} data.target_id - 加密的接收者用户ID（必填）
 * @param {string} data.msg_type - 消息子类型（必填）：TextDef/ImageDef/ImageEmojiDef/ImageNeedAuditDef/ImagePrivateDef/VideoDef
 * @param {string|File} data.content - 消息内容（必填，文本需加密，图片可以是URL或文件）
 * @param {string} [data.quick_msg_id] - 快捷消息ID（可选）
 * @returns {Promise<SendMessageResponse>}
 */
export function sendMessageNew(data) {
  const { target_id, msg_type, content } = data
  const formData = new FormData()
  formData.append('target_id', target_id)
  formData.append('msg_type', msg_type)
  formData.append('meta[content]', encrypt(content))
  return post('/msg/v1/send_msg', formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
    hideLoading: true,
  })
}

/**
 * 更新会话已读状态
 * @param {Object} data - 请求参数
 * @param {string} data.id - 会话ID（conversation_id）
 * @param {number} data.read_type - 消息回执类型：0-进入会话, 1-停留在会话, 2-退出会话
 * @returns {Promise<{target_read_at: string, rank: number}>}
 */
export function markConversationAsRead(data) {
  const formData = new FormData()
  formData.append('id', data.id)
  formData.append('read_type', data.read_type)
  return post('/msg/v1/read', formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
    hideLoading: true,
    hideError: true,
  })
}

/**
 * 上报会话停留时长
 * @param {Object} data - 请求参数
 * @param {string} data.id - 会话ID（conversation_id）
 * @param {number} data.stay_seconds - 停留时长（秒）
 * @returns {Promise<{}>}
 */
export function reportStayDuration(data, config) {
  return post('/msg/v1/report_stay', data, {
    hideLoading: true,
    hideError: true,
    ...config,
  })
}

/**
 * @typedef {Object} InsteadBehaviorResponse
 * @property {number[]} page_stays - 页面停留时长数组（秒）
 */

/**
 * 上报代发消息行为
 * @param {Object} data - 请求参数
 * @param {string} data.instead_source - 代发来源：InsteadSourceClickTab/InsteadSourcePageStay
 * @param {string} [data.source_type] - ClickTab: message/home; PageStay: mine/video_match
 * @param {number} [data.source_value] - PageStay 时表示停留时间（秒）
 * @param {string} [data.s_a] - on=安全模式不代发, off=正常代发
 * @param {Object} [config] - 请求配置
 * @returns {Promise<InsteadBehaviorResponse>}
 */
export function reportInsteadBehavior(data, config) {
  return post('/msg/v1/instead', data, {
    hideLoading: true,
    hideError: true,
    ...config,
  })
}
Object.assign(reportInsteadBehavior, {
  // 代发消息来源类型
  INSTEAD_SOURCE: {
    CLICK_TAB: 'InsteadSourceClickTab',      // 点击消息 Tab
    PAGE_STAY: 'InsteadSourcePageStay',      // 页面停留
  },
  // 代发消息来源类型
  INSTEAD_SOURCE_TYPE: {
    MESSAGE: 'message',
    MESSAGE_TAB: 'message_tab',
    PROFILE: 'mine',
    HOME: 'videolist_tab',
  },
})
